import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export type ClinicMetaConnectionRow = {
  clinic_id: string;
  meta_business_id: string | null;
  waba_id: string | null;
  phone_number_id?: string | null;
  embedded_signup_payload?: Record<string, unknown> | null;
  access_token: string | null;
  access_token_expires_at: string | null;
  status: "connected" | "revoked" | "error";
  connected_at: string;
  connected_by: string | null;
  last_sync_at: string | null;
  last_error: string | null;
  is_enabled: boolean | null;
  template_name: string | null;
  recipient_strategy: "phone_only" | "phone_first" | "bsuid_only" | null;
  send_mode: "template" | "text" | null;
  template_variable_mapping: Record<string, string> | null;
  updated_at: string | null;
};

export function useClinicMetaConnection(clinicId: string | null) {
  return useQuery({
    queryKey: ["clinic-meta-connection", clinicId],
    queryFn: async (): Promise<ClinicMetaConnectionRow | null> => {
      if (!clinicId) return null;
      const { data, error } = await supabase
        .from("clinic_meta_connections")
        .select("*")
        .eq("clinic_id", clinicId)
        .maybeSingle();
      if (error) throw error;
      return (data ?? null) as unknown as ClinicMetaConnectionRow | null;
    },
    enabled: !!clinicId,
    staleTime: 1000 * 30,
  });
}

export function useUpsertClinicMetaConnection() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      clinicId: string;
      wabaId?: string | null;
      phoneNumberId?: string | null;
      status?: string | null;
    }) => {
      const payload = {
        clinic_id: input.clinicId,
        waba_id: input.wabaId ?? null,
        phone_number_id: input.phoneNumberId ?? null,
        status: (input.status ?? "connected") as "connected" | "revoked" | "error",
        last_sync_at: new Date().toISOString(),
      };
      const { error } = await supabase.from("clinic_meta_connections").upsert(payload, {
        onConflict: "clinic_id",
      });
      if (error) throw error;
      return true;
    },
    onSuccess: async (_, variables) => {
      await queryClient.invalidateQueries({ queryKey: ["clinic-meta-connection", variables.clinicId] });
    },
  });
}

export function useDisconnectClinicMetaConnection() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (clinicId: string) => {
      const nowIso = new Date().toISOString();
      const { error } = await supabase
        .from("clinic_meta_connections")
        .upsert(
          {
            clinic_id: clinicId,
            status: "revoked" as const,
            is_enabled: false,
            embedded_signup_payload: null,
            access_token: null,
            access_token_expires_at: null,
            access_token_type: null,
            access_token_scopes: null,
            last_sync_at: nowIso,
            last_error: null,
          },
          { onConflict: "clinic_id" },
        );

      if (error) throw error;
      return true;
    },
    onSuccess: async (_, clinicId) => {
      await queryClient.invalidateQueries({ queryKey: ["clinic-meta-connection", clinicId] });
    },
  });
}
